/**
 * @fileoverview Main EmbeddedApp singleton class.
 * Core of the SDK that manages initialization, state, and module coordination.
 */

import { IFRAME_EVENTS, CONTEXT_EVENTS } from "./events";
import { UI_EVENTS } from "../modules/ui/events";
import {
  postToHost,
  waitForMessage,
  removeAllListeners,
  isInIframe,
  onMessage,
  dispatchLocal,
} from "./messenger";
import { CHECKOUT_EVENTS } from "../modules/checkout/events";
import type {
  InitOptions,
  EmbeddedState,
  LayoutInfo,
  ContextProvideResponse,
  ThemeChangeMessage,
  ConfirmResponseMessage,
  InitCallback,
} from "./types";
import { handleResponse, cancelAllRequests } from "./requests";
import { logger, setLoggerConfig } from "./logger";
import { createSubscription, type Subscription } from "./subscriptions";
import { inferDirFromLocale } from "./constants";

// Import modules
import { createAuthModule, type AuthModule } from "../modules/auth";
import { createPageModule, type PageModule } from "../modules/page";
import { createNavModule, type NavModule } from "../modules/nav";
import { createUIModule, type UIModule } from "../modules/ui";
import { createCheckoutModule, type CheckoutModule } from "../modules/checkout";

/**
 * Default layout values.
 */
const DEFAULT_LAYOUT: LayoutInfo = {
  theme: "light",
  dir: "rtl",
  width: 0,
  locale: "ar",
  currency: "SAR",
};

/**
 * Post-init hook type. Called after handshake with context response.
 */
type PostInitHook = (response: ContextProvideResponse) => void;

/**
 * Main Embedded SDK class.
 * Provides the primary interface for third-party apps to communicate with the Salla host.
 */
class EmbeddedApp {
  private initialized = false;
  private initializing = false;
  private debugMode = false;
  private appReady = false;
  private layout: LayoutInfo = { ...DEFAULT_LAYOUT };

  // Subscriptions
  private themeSubscription: Subscription<(theme: "light" | "dark") => void>;
  private initSubscription: Subscription<InitCallback>;

  // Post-init hooks for modules to register
  private postInitHooks: PostInitHook[] = [];

  /** Auth module for token management */
  public auth: AuthModule;
  /** Page module for navigation */
  public page: PageModule;
  /** Nav module for primary actions */
  public nav: NavModule;
  /** UI module for loading, overlay, toast, modal */
  public ui: UIModule;
  /** Checkout module for checkout flow */
  public checkout: CheckoutModule;

  constructor() {
    // Create subscriptions
    this.themeSubscription = createSubscription();
    this.initSubscription = createSubscription();

    // Initialize modules
    this.auth = createAuthModule(() => this.getState());
    this.page = createPageModule();
    this.nav = createNavModule();
    this.ui = createUIModule();
    this.checkout = createCheckoutModule();

    // Register checkout's post-init hook for pending checkout results
    this.registerPostInitHook((response) => {
      const result = response.payload.pendingCheckoutResult;
      if (result) {
        logger.debug("Dispatching pending checkout result:", result);
        // Dispatch on next microtask to allow callbacks to register after init
        queueMicrotask(() => {
          dispatchLocal(CHECKOUT_EVENTS.RESPONSE, {
            success: result.success,
            status: result.status,
            error: result.error,
            context: result.context,
          });
        });
      }
    });

    // Set up core listeners
    this.setupListeners();
  }

  /**
   * Register a hook to run after successful init handshake.
   * Used by modules to process context data.
   */
  private registerPostInitHook(hook: PostInitHook): void {
    this.postInitHooks.push(hook);
  }

  /**
   * Set up core event listeners.
   */
  private setupListeners(): void {
    // Theme changes
    onMessage<ThemeChangeMessage>(CONTEXT_EVENTS.THEME_CHANGE, (data) => {
      this.layout.theme = data.payload.theme;
      logger.debug("Theme changed:", data.payload.theme);
      this.themeSubscription.notify(data.payload.theme);
    });

    // Confirm dialog responses
    onMessage<ConfirmResponseMessage>(UI_EVENTS.CONFIRM_RESPONSE, (data) => {
      logger.debug("Received confirm response:", data);
      if (data.requestId) {
        handleResponse(data.requestId, { confirmed: data.payload.confirmed });
      }
    });
  }

  /**
   * Get current SDK state.
   */
  getState(): Readonly<EmbeddedState> {
    return {
      ready: this.initialized,
      initializing: this.initializing,
      layout: { ...this.layout },
    };
  }

  /**
   * Check if SDK is initialized and ready.
   */
  isReady(): boolean {
    return this.initialized;
  }

  /**
   * Subscribe to theme changes.
   */
  onThemeChange(callback: (theme: "light" | "dark") => void): () => void {
    return this.themeSubscription.subscribe(callback);
  }

  /**
   * Subscribe to init completion. Fires immediately if already initialized.
   */
  onInit(callback: InitCallback): () => void {
    if (this.initialized) {
      try {
        callback(this.getState());
      } catch (error) {
        logger.error("Error in init callback:", error);
      }
    }
    return this.initSubscription.subscribe(callback);
  }

  /**
   * Signal that the app is fully loaded and ready.
   */
  ready(): void {
    if (this.appReady) {
      logger.debug("App already signaled as ready");
      return;
    }
    if (!this.initialized) {
      logger.warn("Cannot signal ready before init() is called");
      return;
    }
    this.appReady = true;
    postToHost(IFRAME_EVENTS.READY, {});
    logger.debug("Sent ready signal to host");
  }

  /**
   * Initialize the SDK and establish connection with the host.
   */
  async init(options: InitOptions = {}): Promise<{ layout: LayoutInfo }> {
    // Already initialized
    if (this.initialized) {
      logger.debug("Already initialized, returning current layout");
      return { layout: { ...this.layout } };
    }

    // Initialization in progress - wait for it
    if (this.initializing) {
      logger.warn("Initialization already in progress");
      return new Promise((resolve) => {
        const unsubscribe = this.onInit((state) => {
          unsubscribe();
          resolve({ layout: { ...state.layout } });
        });
      });
    }

    // Start initialization
    this.initializing = true;
    this.debugMode = options.debug ?? false;
    setLoggerConfig({ debug: this.debugMode });

    if (!isInIframe()) {
      logger.warn("Not running in an iframe. Some features may not work.");
    }

    logger.debug("Initializing SDK...");

    try {
      // Handshake: send init, wait for context
      postToHost(IFRAME_EVENTS.INIT, {
        height: document.documentElement.scrollHeight,
      });
      logger.debug("Sent iframe.ready message, waiting for context...");

      const response = await waitForMessage<ContextProvideResponse>(
        CONTEXT_EVENTS.PROVIDE,
      );
      logger.debug("Received context from host:", response);

      // Update layout
      const { layout } = response.payload;
      this.layout = {
        theme: layout?.theme ?? "light",
        dir: layout?.dir ?? inferDirFromLocale(layout?.locale),
        width: layout?.width ?? 0,
        locale: layout?.locale ?? "ar",
        currency: layout?.currency ?? "SAR",
      };

      // Run post-init hooks (e.g., pending checkout handling)
      this.postInitHooks.forEach((hook) => {
        try {
          hook(response);
        } catch (error) {
          logger.error("Error in post-init hook:", error);
        }
      });

      this.initialized = true;
      this.initializing = false;

      logger.debug("Initialization complete. Layout:", this.layout);

      // Notify subscribers
      this.initSubscription.notify(this.getState());

      return { layout: { ...this.layout } };
    } catch (error) {
      this.initializing = false;
      throw error;
    }
  }

  /**
   * Destroy the SDK instance and clean up resources.
   */
  destroy(): void {
    logger.debug("Destroying SDK instance");

    if (this.initialized) {
      postToHost(IFRAME_EVENTS.DESTROY, {});
      logger.debug("Sent destroy event to host");
    }

    // Cleanup modules
    this.checkout.destroy();
    this.nav.destroy();

    // Cleanup core resources
    cancelAllRequests("SDK destroyed");
    removeAllListeners();
    this.themeSubscription.clear();
    this.initSubscription.clear();
    this.postInitHooks = [];

    // Reset state
    this.initialized = false;
    this.initializing = false;
    this.appReady = false;
    this.layout = { ...DEFAULT_LAYOUT };
  }
}

// Singleton
let instance: EmbeddedApp | null = null;

/**
 * Get the singleton EmbeddedApp instance.
 */
export function getEmbeddedApp(): EmbeddedApp {
  if (!instance) {
    instance = new EmbeddedApp();
  }
  return instance;
}

/**
 * Reset the singleton (for testing).
 */
export function resetEmbeddedApp(): void {
  if (instance) {
    instance.destroy();
    instance = null;
  }
}

export { EmbeddedApp };
